import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { User, LogOut, LayoutDashboard, Settings, Shield, Menu, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_nice-grading-app/artifacts/iks4rsh0_Nice%20logo%200925%20%283%29.jpg';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isLoading, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      logout();
    }, 100);
  };

  const handleStartSubmission = () => {
    if (isAuthenticated) {
      navigate('/submit');
    } else {
      navigate('/login', { state: { from: { pathname: '/submit' } } });
    }
  };

  const handleMobileNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src={LOGO_URL} 
              alt="NICE Grading" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-[#003594] ${
                isActive('/') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/how-it-works" 
              className={`text-sm font-medium transition-colors hover:text-[#003594] ${
                isActive('/how-it-works') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              How It Works
            </Link>
            {/* Grading Standards - Hidden until content is ready. Access at /grading-standards */}
            <Link 
              to="/dealer-program" 
              className={`text-sm font-medium transition-colors hover:text-[#003594] ${
                isActive('/dealer-program') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              Dealer Program
            </Link>
            
            <Button 
              onClick={handleStartSubmission}
              className="bg-[#003594] hover:bg-[#002554] text-white px-6 rounded-md"
            >
              Start Submission
            </Button>
            
            {!isLoading && (
              <>
                {isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="w-10 h-10 rounded-full bg-[#003594] hover:bg-[#002554] flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#003594] focus:ring-offset-2">
                        <User className="w-5 h-5 text-white" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <div className="px-2 py-2 text-sm text-gray-500">
                        Signed in as<br />
                        <span className="font-medium text-gray-900">{user?.name}</span>
                        {isAdmin() && (
                          <span className="ml-1 text-xs bg-[#003594] text-white px-1.5 py-0.5 rounded">Admin</span>
                        )}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => navigate('/dashboard')}
                        className="cursor-pointer"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        My Dashboard
                      </DropdownMenuItem>
                      {isAdmin() && (
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className="cursor-pointer text-[#003594]"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => navigate('/settings')}
                        className="cursor-pointer"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Log Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link 
                    to="/login" 
                    className="text-sm font-medium text-gray-600 hover:text-[#003594] transition-colors"
                  >
                    Log In
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-3">
            <Button 
              onClick={handleStartSubmission}
              className="bg-[#003594] hover:bg-[#002554] text-white px-4 py-2 text-sm rounded-md"
            >
              Start Submission
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-[#003594] hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100">
          <div className="px-4 py-4 space-y-3">
            {/* Navigation Links */}
            <Link 
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/how-it-works"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/how-it-works') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              How It Works
            </Link>
            {/* Grading Standards - Hidden until content is ready. Access at /grading-standards */}
            <Link 
              to="/dealer-program"
              onClick={() => setMobileMenuOpen(false)}
              className={`block py-2 text-base font-medium transition-colors ${
                isActive('/dealer-program') ? 'text-[#003594]' : 'text-gray-600'
              }`}
            >
              Dealer Program
            </Link>

            {/* Divider */}
            <div className="border-t border-gray-200 my-3"></div>

            {/* User section */}
            {!isLoading && (
              <>
                {isAuthenticated ? (
                  <>
                    <div className="py-2 text-sm text-gray-500">
                      Signed in as <span className="font-medium text-gray-900">{user?.name}</span>
                      {isAdmin() && (
                        <span className="ml-1 text-xs bg-[#003594] text-white px-1.5 py-0.5 rounded">Admin</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleMobileNavClick('/dashboard')}
                      className="flex items-center w-full py-2 text-base font-medium text-gray-600"
                    >
                      <LayoutDashboard className="w-5 h-5 mr-3" />
                      My Dashboard
                    </button>
                    {isAdmin() && (
                      <button
                        onClick={() => handleMobileNavClick('/admin')}
                        className="flex items-center w-full py-2 text-base font-medium text-[#003594]"
                      >
                        <Shield className="w-5 h-5 mr-3" />
                        Admin Panel
                      </button>
                    )}
                    <button
                      onClick={() => handleMobileNavClick('/settings')}
                      className="flex items-center w-full py-2 text-base font-medium text-gray-600"
                    >
                      <Settings className="w-5 h-5 mr-3" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full py-2 text-base font-medium text-red-600"
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Log Out
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-base font-medium text-gray-600"
                  >
                    Log In
                  </Link>
                )}
              </>
            )}

            {/* CTA Button removed - now in header */}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
